import { change } from 'redux-form';
import type {
	CardAction,
	CardData,
	CardFlowEvent,
	StoryModel,
	StorySettingsType,
	StoryStep,
	StoryVersionType,
} from 'types/story';
import { UPDATE_EDITABLE_STORY } from 'admin/constants/actions';
import { AUTO_SYNC_TARGET, FORM_MODEL } from 'admin/constants/common';
import type { AdminReducerState, AdminThunkDispatch } from 'admin/reducers';
import type { EditCaptureMark } from 'admin/utils/story-edit-operations/save-capture';

/**
 * Local write into the story editor.
 *
 * Autosave (`storyEditorSync`) sends `storyEditor.story` to the PUT endpoints
 * (main row + latest version); edit operations are captured from the same model.
 * `form.storyEditor` is the Story Settings editing buffer, also read by a few form-based views.
 * Any write that did not already come from redux-form itself should set `updateForm: true`,
 * so those views do not show stale values.
 *
 * `updateForm: false` is correct only when the form is already the source of the change.
 *
 * `entire: true` replaces the whole `story` and skips the form update (`path` becomes undefined).
 * Story Settings uses it to commit its normalized form values.
 */
export type EditableStoryData =
	| Partial<StoryModel>
	| Partial<StoryVersionType>
	| StoryModel[keyof StoryModel]
	| CardData
	| Partial<CardAction>
	| CardFlowEvent[]
	| StoryStep[]
	| NonNullable<StorySettingsType['variables']>['custom'];

export type Payload = {
	/** When true, also write `form.storyEditor` at the same path. Required for chat / programmatic edits. */
	updateForm?: boolean;
	/** Replace entire `StoryModel`. Skips form update. */
	entire?: boolean;
	/** Lodash path under the story (or under the current version when `isVersionPath` is true). */
	path?: string;
	/**
	 * Default true: prefix `path` with `storyVersions.<currentVersion>`.
	 * False for root fields (`name` / `type` / `language`).
	 */
	isVersionPath?: boolean;
	/**
	 * Value written at `path` via lodash `set`.
	 * Whole `StoryModel` (`entire`), `StoryVersionType` slice, or a nested field
	 * (root `name`/`type`/`language`, card, steps, events, settings fragment, …).
	 */
	data: EditableStoryData;
	/** Exclude this write from edit operation capture (see edit-operations middleware). */
	editCapture?: EditCaptureMark;
};

type StoryEditorChangePayload = {
	data: Payload['data'];
	path?: string;
};

/**
 * Update storyEditor reducer.
 */
export const storyEditorChange = (payload: StoryEditorChangePayload) =>
	({ type: UPDATE_EDITABLE_STORY.UPDATE, payload }) as const;

/**
 * Update form.storyEditor reducer.
 */
export const storyEditorFormChange = ({ data, path }: { data: Payload['data']; path: string }) =>
	change(FORM_MODEL.EDIT_STORY, path, data);

/**
 * Update current story version, a field on it, a root story field, or the entire story.
 * Triggers `AUTO_SYNC_TARGET.STORY_EDITOR` after the reducer write.
 */
export function updateEditableStory({
	data,
	path = '',
	entire = false,
	updateForm = false,
	isVersionPath = true,
	editCapture,
}: Payload) {
	return (dispatch: AdminThunkDispatch, getState: () => AdminReducerState): void => {
		const version = getState().version.current;

		const pathPattern = isVersionPath ? `storyVersions.${version}${path ? `.${path}` : ''}` : path;
		const _path = entire ? undefined : pathPattern;

		if (updateForm && _path !== undefined) {
			dispatch(storyEditorFormChange({ data, path: _path }));
		}

		dispatch({
			...storyEditorChange({ data, path: _path }),
			meta: { autoSync: AUTO_SYNC_TARGET.STORY_EDITOR, editCapture },
		});
	};
}
